/*
 * Runtime config for the AquaStar customer web app.
 *
 * Mirrors the admin console's pattern. The single appId 'aquastar' ties
 * this SPA to the same Gateway-side App-Definitions bundle and per-app
 * DB used by the iOS app and the admin console — never hard-code the
 * string elsewhere.
 *
 *   iOS sends:   { "appId": "aquastar" }     (Network+Auth.swift)
 *   App-Defs:    App-Definitions/aquastar/1.0.0/manifest.json
 *   Per-app DB:  app_aquastar_<stage>
 */

export const APP_ID = 'aquastar' as const;

/**
 * Gateway base URL.
 *
 * Production builds: empty string. The deployed SPA sits behind Caddy,
 * which reverse-proxies `/auth/*` and `/v1/*` to the Gateway on the
 * same origin. Same-origin = no preflight, no CORS dance. This is the
 * trick Vault Web and the admin app use.
 *
 * Local dev (`npm run dev` against vite's localhost server): set
 * VITE_GATEWAY_BASE in `.env.development.local` to e.g.
 * `https://gateway-dev.kstack.app` so the dev server can reach a
 * remote Gateway directly. CORS may bite — set up a Vite proxy in
 * vite.config.ts under `server.proxy` for that case.
 */
export const GATEWAY_BASE: string =
  (import.meta.env.VITE_GATEWAY_BASE as string | undefined)?.replace(/\/+$/, '') ??
  '';

/** App-scoped function URL — every customer-facing handler lives here. */
export function appFnUrl(name: string): string {
  return `${GATEWAY_BASE}/v1/app/${APP_ID}/fn/${name}`;
}

/**
 * Alias URL — for handlers that take a trailing path segment
 * (parameterized aliases declared in the manifest, e.g.
 * `/devices/device-info/:deviceId`). The canonical `/fn/<name>` route
 * does NOT accept trailing segments.
 *
 * Uses the iOS-compat `/api/v1/...` prefix that the Gateway's
 * `aquastarLegacyApiPrefixStripper` middleware accepts and strips
 * down to the bare alias path (`/devices/device-info/<mac>`), which
 * the alias-cache middleware then matches against the manifest-
 * declared aliases (which are stored WITHOUT any `/v1/app/<appId>`
 * prefix — they're app-agnostic at the URL level).
 *
 *   appAliasUrl('/devices/device-info/' + encodeURIComponent(mac))
 *   → /api/v1/devices/device-info/<mac>
 *
 * Why not `/v1/app/<appId>/devices/...`? That URL never matches
 * anything — neither the dispatcher (which expects `/fn/<name>`) nor
 * the alias middleware (which compares against the bare alias path).
 * Bench session 2026-04-28 confirmed it returns Express's default
 * 404 because the request never reaches the app-functions router.
 */
export function appAliasUrl(aliasPath: string): string {
  const path = aliasPath.startsWith('/') ? aliasPath : `/${aliasPath}`;
  return `${GATEWAY_BASE}/api/v1${path}`;
}

/** SSE stream URL — declared in the AquaStar manifest's `streams[]`. */
export function appStreamUrl(name: string): string {
  return `${GATEWAY_BASE}/v1/app/${APP_ID}/stream/${name}`;
}

export const STAGE: 'dev' | 'uat' | 'prod' =
  ((import.meta.env.VITE_STAGE as string | undefined) ?? 'dev') as
    | 'dev'
    | 'uat'
    | 'prod';
